import React from 'react'
import { Link } from '@inertiajs/react'
import OperatorLayout from '@/layouts/OperatorLayout'
import { HelpTooltip } from '@/components/HelpTooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts'
import {
  Target,
  TrendingDown,
  TrendingUp,
  Activity,
  AlertTriangle,
  Info,
  ArrowLeft,
  Clock,
  Gauge,
  CalendarDays,
  ShieldCheck,
  ShieldAlert,
  BadgePoundSterling,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { currencySymbol } from '@/lib/calendar-utils'
import { formatMoneySym, formatCount } from '@/lib/format'
import { dormantReasonLabel, velocityDescriptor, gapPhrase } from '@/lib/target-utils'
import { formatPoints } from '@/lib/plain-language'
import EngineDetails from '@/components/EngineDetails'
import type { AdrScope, SharedProps } from '@/types/shared'

// ─── Types ────────────────────────────────────────────────────────────────────

interface SavedTargetValues {
  occupancy: number | null
  adr: number | null
  revpar: number | null
}

// D13 day-type breakdown jsonb (string keys from the backend). Implied targets + unit-night
// mix come from the calculator; forecast/gap rollups are added by the snapshot writer (C6).
// Every field is optional — pre-decomposition (flat-mix / legacy) snapshots may omit them.
interface DayTypeBreakdown {
  r?: number
  target_weekday?: number
  target_weekend?: number
  unit_nights_weekday?: number
  unit_nights_weekend?: number
  forecast_weekday?: number | null
  forecast_weekend?: number | null
  gap_weekday?: number | null
  gap_weekend?: number | null
  // ── Target v3 (C7) — ADR floor provenance + display-only RevPAR projection ──
  adr_floor_source?: 'explicit' | 'default' | null
  adr_floor_pct?: number | null
  adr_floor_weekday?: number | null
  adr_floor_weekend?: number | null
  adr_reference_weekday?: number | null
  adr_reference_weekend?: number | null
  projected_revpar?: number | null
}

interface ForecastSnapshot {
  targetMonth: number
  targetYear: number
  computedAt: string
  forecastEom: number | null
  targetEom: number | null
  targetGap: number | null
  state: string | null
  dormantReason: string | null
  monthConfidence: number | null
  capTrimmedPct: number | null
  atFloorPct: number | null
  capTrimmed: boolean
  // ── Target v3 (C6) — nil on pre-v3 snapshots; render conditionally ──
  forecastP10: number | null
  forecastP90: number | null
  deadbandPpt: number | null
  deadbandSource: 'band' | 'horizon' | null
  velocityRatio: number | null
  velocityRelevance: number | null
  dayTypeBreakdown: DayTypeBreakdown | null
  // ── Target v3 (C7) — ADR floor guardrail + display-only projections ──
  adrFloorAmount: number | null
  adrLimitedPct: number | null
  projectedAdr: number | null
  // ── Target v3 (C8) — dilution-guard rollup; nil on pre-C8 snapshots ──
  dilutionGuardedPct: number | null
}

interface AffectedDate {
  date: string
  roomTypeName: string
  ratePlanName: string
  beforeAmount: number | null
  afterAmount: number
  modifierPct: number
  // C7 — whether the ADR floor held this date's discount back, and the floor it held at.
  adrLimited: boolean
  adrFloorAmount: number | null
  // C8 — whether the dilution guard withheld this date's discount (price-inelastic close-in),
  // plus the lead time so the row can say "at N days out". modifierPct is 0 on a guarded date.
  dilutionGuarded: boolean
  leadTimeDays: number | null
}

// ── Target v3 (C9, D17) — the accuracy ledger / forecast calibration ──
// Per-horizon record of how the engine's projections compared to what actually happened,
// and the bias correction (operator label: "forecast calibration") applied at each horizon.
// Every value is pre-computed by the backend — the panel does zero math on it.
interface AccuracyLedgerBucket {
  horizonBucket: string
  label: string
  nMonths: number
  meanErrorPpt: number | null
  maePpt: number | null
  appliedCorrectionPpt: number
}

interface AccuracyLedger {
  buckets: AccuracyLedgerBucket[]
  hasData: boolean
  currentHorizonBucket: string | null
  currentDaysUntilEom: number
  currentAppliedPpt: number | null
  shrinkN0: number
  maxPpt: number
}

interface TargetDetailProps extends SharedProps {
  month: number
  monthName: string
  year: number
  savedTargets: SavedTargetValues
  forecastSnapshots: ForecastSnapshot[]
  latestSnapshot: ForecastSnapshot | null
  affectedDates: AffectedDate[]
  // C9 (D17) — accuracy ledger; null on properties with no pricing settings.
  accuracyLedger: AccuracyLedger | null
  /** Combined inventory: ADR is the building's, and the floor is in the lead listing's prices. */
  adrScope?: AdrScope | null
  // v1 fields — kept for graceful degradation, always nil on v2
  mtdSignals: null
  mtdState: null
  timeline: []
  mtdChartData: []
}

// ─── State config (v2 vocabulary) ────────────────────────────────────────────

const STATE_CONFIG = {
  on_track:  { label: 'On track',   className: 'bg-slate-100 text-slate-700',    icon: Activity },
  pushing:   { label: 'Pushing',    className: 'bg-rose-100 text-rose-700',      icon: TrendingDown },
  easing:    { label: 'Easing',     className: 'bg-emerald-100 text-emerald-700', icon: TrendingUp },
  at_floor:  { label: 'At floor',   className: 'bg-amber-100 text-amber-700',    icon: AlertTriangle },
  dormant:   { label: 'Dormant',    className: 'bg-slate-100 text-slate-500',    icon: Clock },
} as const

type StateKey = keyof typeof STATE_CONFIG

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateShort(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function formatDatetime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function formatTimeShort(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 2) return 'just now'
  if (diffMin < 60) return `${diffMin} min ago`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

// ─── Forecast Trajectory chart ────────────────────────────────────────────────

interface ForecastTooltipProps {
  active?: boolean
  payload?: any[]
  label?: string
}

function ForecastTooltip({ active, payload, label }: ForecastTooltipProps) {
  if (!active || !payload?.length) return null
  const forecast = payload.find((p: any) => p.dataKey === 'forecastEom')?.value
  const target = payload.find((p: any) => p.dataKey === 'targetEom')?.value
  // The band series carries the raw [p10, p90] tuple from the backend — read both endpoints
  // verbatim, no arithmetic.
  const band = payload.find((p: any) => p.dataKey === 'band')?.value as
    | [number, number]
    | undefined
  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2 shadow-lg shadow-black/5">
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      {forecast != null && (
        <p className="text-sm font-semibold text-[#0d9488]">{forecast}% projected EOM</p>
      )}
      {Array.isArray(band) && band[0] != null && band[1] != null && (
        <p className="text-[11px] text-[#0d9488]/70">
          Band: {band[0]}%–{band[1]}%
        </p>
      )}
      {target != null && (
        <p className="text-xs text-slate-400">Target: {target}%</p>
      )}
    </div>
  )
}

interface ForecastTrajectoryChartProps {
  snapshots: ForecastSnapshot[]
  targetEom: number | null
}

function ForecastTrajectoryChart({ snapshots, targetEom }: ForecastTrajectoryChartProps) {
  if (snapshots.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Clock className="mx-auto h-8 w-8 text-muted-foreground/40" />
        <p className="mt-3 text-sm font-medium text-foreground">No forecast history yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Forecast snapshots accumulate each time the rate engine runs. Check back shortly.
        </p>
      </div>
    )
  }

  // Format snapshots for chart — x-axis is date label, forecast + target lines, and the D11
  // forecast band as a [p10, p90] range area. The band tuple is just the two backend values
  // packaged into an array (no arithmetic); Recharts draws the shaded span between them.
  const data = snapshots.map((s) => ({
    label: formatDatetime(s.computedAt),
    forecastEom: s.forecastEom,
    targetEom: s.targetEom ?? targetEom,
    band:
      s.forecastP10 != null && s.forecastP90 != null
        ? ([s.forecastP10, s.forecastP90] as [number, number])
        : undefined,
  }))

  const hasBand = data.some((d) => d.band != null)

  // Determine y-axis domain with some padding — include band endpoints so the shaded range
  // is never clipped. Pure axis-layout math; the displayed numbers are still backend-verbatim.
  const allValues = data
    .flatMap((d) => [d.forecastEom, d.targetEom, d.band?.[0], d.band?.[1]])
    .filter((v): v is number => v != null)
  const minVal = allValues.length > 0 ? Math.floor(Math.min(...allValues) - 5) : 0
  const maxVal = allValues.length > 0 ? Math.ceil(Math.max(...allValues) + 5) : 100

  return (
    <div className="pt-2">
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={data}>
          <defs>
            <linearGradient id="forecastBand" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0d9488" stopOpacity={0.16} />
              <stop offset="100%" stopColor="#0d9488" stopOpacity={0.06} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 9, fill: '#94a3b8' }}
            tickLine={false}
            axisLine={{ stroke: '#e2e8f0' }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => `${v}%`}
            domain={[minVal, maxVal]}
            width={42}
          />
          <RechartsTooltip content={<ForecastTooltip />} />
          <Legend iconType="line" wrapperStyle={{ fontSize: 11, color: '#64748b', paddingTop: 8 }} />
          {/* D11 forecast band — shaded P10–P90 range around the projected line. Only drawn
              when at least one snapshot carries a band (pre-v3 history renders without it). */}
          {hasBand && (
            <Area
              type="monotone"
              dataKey="band"
              name="Forecast band"
              stroke="none"
              fill="url(#forecastBand)"
              connectNulls
              activeDot={false}
              isAnimationActive={false}
            />
          )}
          <Line
            type="monotone"
            dataKey="targetEom"
            name="Target"
            stroke="#94a3b8"
            strokeDasharray="5 3"
            strokeWidth={1.5}
            dot={false}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="forecastEom"
            name="Projected EOM"
            stroke="#0d9488"
            strokeWidth={2}
            dot={false}
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

// ─── Affected dates table ─────────────────────────────────────────────────────

interface AffectedDatesProps {
  dates: AffectedDate[]
  totalCount: number
  currency: string
  learnMoreHref: string
}

function AffectedDatesTable({ dates, totalCount, currency, learnMoreHref }: AffectedDatesProps) {
  const sym = currencySymbol(currency)
  const anyLimited = dates.some((d) => d.adrLimited)
  const anyGuarded = dates.some((d) => d.dilutionGuarded)

  if (dates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <Target className="mx-auto h-8 w-8 text-muted-foreground/40" />
        <p className="mt-3 text-sm font-medium text-foreground">No upcoming dates with target pressure</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Dates appear here when the engine applies a non-zero target pressure modifier.
        </p>
      </div>
    )
  }

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Date</TableHead>
            <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Room type</TableHead>
            <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Plan</TableHead>
            <TableHead className="text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Before</TableHead>
            <TableHead className="text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">After</TableHead>
            <TableHead className="text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Modifier</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dates.map((row, idx) => {
            const isNeg = row.modifierPct < 0
            return (
              <TableRow key={idx}>
                <TableCell className="text-sm font-medium text-foreground tabular-nums">
                  {formatDateShort(row.date)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{row.roomTypeName}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{row.ratePlanName}</TableCell>
                <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                  {row.beforeAmount != null ? formatMoneySym(row.beforeAmount, sym) : '—'}
                </TableCell>
                <TableCell className="text-right text-sm font-semibold tabular-nums text-foreground">
                  {formatMoneySym(row.afterAmount, sym)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex flex-col items-end gap-1">
                    {/* C8 — a dilution-guarded date carries a 0% modifier (discount fully withheld);
                        show "Withheld" rather than a misleading "0.0%". Otherwise the signed % badge. */}
                    {row.dilutionGuarded ? (
                      <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600">
                        Withheld
                      </span>
                    ) : (
                      <span className={cn(
                        'inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
                        isNeg ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                      )}>
                        {row.modifierPct > 0 ? '+' : ''}{row.modifierPct.toFixed(1)}%
                      </span>
                    )}
                    {/* C8 — dilution guard: demand at this lead books regardless of price. Wins over
                        the floor badge (a guarded date had its WHOLE discount withheld). Copy is the
                        locked day-detail vocabulary. */}
                    {row.dilutionGuarded ? (
                      <span
                        className="inline-flex items-center gap-1 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700"
                        title={
                          row.leadTimeDays != null
                            ? `Discount withheld — at ${row.leadTimeDays} day${row.leadTimeDays === 1 ? '' : 's'} out, demand here books regardless of price; cutting would only cheapen rooms that would have sold.`
                            : 'Discount withheld — demand here books regardless of price; cutting would only cheapen rooms that would have sold.'
                        }
                      >
                        <ShieldAlert className="h-3 w-3" />
                        Discount held back
                      </span>
                    ) : (
                      /* C7 — explain a floor-limited date inline (the discount was withheld). */
                      row.adrLimited && (
                        <span
                          className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                          title={
                            row.adrFloorAmount != null
                              ? `Discount held at the ADR floor (${sym}${row.adrFloorAmount.toFixed(2)}) — the occupancy target wanted a deeper cut than your margin floor allows.`
                              : 'Discount held at the ADR floor — the occupancy target wanted a deeper cut than your margin floor allows.'
                          }
                        >
                          <ShieldCheck className="h-3 w-3" />
                          ADR floor
                        </span>
                      )
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {(anyLimited || anyGuarded || totalCount > 30) && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/40 px-4 py-2.5">
          <div className="flex flex-col gap-1">
            {anyGuarded && (
              <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <ShieldAlert className="h-3.5 w-3.5 text-sky-600" />
                <span>
                  <span className="font-medium text-sky-700">Discount held back</span> on these dates —
                  rooms sell regardless of price, so a cut would only cheapen rooms that would have sold.{' '}
                  <a href={learnMoreHref} className="font-medium text-[#0d9488] hover:underline">
                    Learn more
                  </a>
                </span>
              </p>
            )}
            {anyLimited && (
              <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-amber-600" />
                <span>
                  <span className="font-medium text-amber-700">ADR floor</span> dates had their discount held
                  back to protect ADR.{' '}
                  <a href={learnMoreHref} className="font-medium text-[#0d9488] hover:underline">
                    Learn more
                  </a>
                </span>
              </p>
            )}
          </div>
          {totalCount > 30 && (
            <p className="text-xs text-muted-foreground">Showing 30 of {formatCount(totalCount)} dates with target pressure</p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Stat card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string
  value: string
  sub?: string
  gapDelta?: number | null
}

function StatCard({ label, value, sub, gapDelta }: StatCardProps) {
  return (
    <div className="rounded-xl border border-border/60 bg-white px-5 py-4 shadow-sm">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
      {gapDelta != null && (
        <p className={cn(
          'mt-1 text-xs font-semibold tabular-nums',
          gapDelta >= 0 ? 'text-emerald-600' : 'text-rose-600'
        )}>
          {gapPhrase(gapDelta)}
        </p>
      )}
    </div>
  )
}

// ─── Booking velocity chip (D12) ──────────────────────────────────────────────

// Compact chip for the header. Renders nothing on pre-v3 snapshots (nil velocity) so old
// history never shows a misleading 1.0×. The multiplier is shown exactly as the backend sent it.
function VelocityChip({
  ratio,
  relevance,
  learnMoreHref,
}: {
  ratio: number | null
  relevance: number | null
  learnMoreHref: string
}) {
  const v = velocityDescriptor(ratio, relevance)
  if (!v) return null

  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
          v.chipClass,
        )}
      >
        <Gauge className="h-3 w-3" />
        Booking velocity {v.display}
        <span className="font-medium opacity-80">— {v.phrase}</span>
      </span>
      <HelpTooltip
        content={v.relevanceNote ? `${v.tooltip} ${v.relevanceNote}` : v.tooltip}
        learnMore={learnMoreHref}
      />
    </span>
  )
}

// plain-language-ignore-start — LatestSnapshotPanel + DayTypeBreakdownCard are diagnostics-only
// components: defined here at top level, but MOUNTED only inside `<EngineDetails variant="section">`
// (see the "Engine diagnostics" basement below). Their `±…ppt` noise-floor and `…ppt` gap cells are
// verbatim Tier-3 raw values, off the glance surface by construction. Do NOT widen this region to
// cover AdrFloorPanel or any other glance component. See docs/revenue-vocabulary.md.
// ─── Latest-snapshot panel: forecast band + velocity + noise-floor source (D11/D12) ──────────

function LatestSnapshotPanel({
  snap,
  learnMoreHref,
}: {
  snap: ForecastSnapshot
  learnMoreHref: string
}) {
  const hasBand = snap.forecastP10 != null && snap.forecastP90 != null
  const v = velocityDescriptor(snap.velocityRatio, snap.velocityRelevance)

  // Nothing v3 to show on a pre-v3 snapshot — render nothing so the page is backward-compatible.
  if (!hasBand && !v && snap.deadbandPpt == null && snap.monthConfidence == null) return null

  const deadbandLabel =
    snap.deadbandSource === 'band'
      ? 'Band-based'
      : snap.deadbandSource === 'horizon'
        ? 'Horizon fallback'
        : '—'

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {/* Projection confidence (raw signal strength — diagnostics-only) */}
      <div className="rounded-lg border border-border/60 bg-slate-50/60 px-4 py-3">
        <div className="flex items-center gap-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Confidence
          </p>
          <HelpTooltip
            content="How much the engine trusts this month’s pickup forecast, on a 0–100% scale. Low confidence means the projection is standing by; it may be mostly silent at long horizons."
            learnMore={learnMoreHref}
          />
        </div>
        <p className="mt-1 text-base font-bold tabular-nums text-foreground">
          {snap.monthConfidence != null ? `${Math.round(snap.monthConfidence * 100)}%` : '—'}
        </p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">forecast signal strength</p>
      </div>

      {/* Forecast band */}
      <div className="rounded-lg border border-border/60 bg-slate-50/60 px-4 py-3">
        <div className="flex items-center gap-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Forecast band
          </p>
          <HelpTooltip
            content="The plausible range around the projected end-of-month (P10–P90), drawn from your property’s own pickup variability. A wide band means pickup at this horizon is hard to predict."
            learnMore={learnMoreHref}
          />
        </div>
        {hasBand ? (
          <p className="mt-1 text-base font-bold tabular-nums text-foreground">
            {snap.forecastP10}% <span className="text-muted-foreground font-medium">–</span>{' '}
            {snap.forecastP90}%
          </p>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">Not yet measured</p>
        )}
        <p className="mt-0.5 text-[11px] text-muted-foreground">around the projected end-of-month</p>
      </div>

      {/* Booking velocity */}
      <div className="rounded-lg border border-border/60 bg-slate-50/60 px-4 py-3">
        <div className="flex items-center gap-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Booking velocity
          </p>
          <HelpTooltip
            content={v ? (v.relevanceNote ? `${v.tooltip} ${v.relevanceNote}` : v.tooltip) : 'Recent observed pickup vs curve-expected pickup over the trailing window. 1.0× is on tempo.'}
            learnMore={learnMoreHref}
          />
        </div>
        {v ? (
          <>
            <p
              className={cn(
                'mt-1 text-base font-bold tabular-nums',
                v.tone === 'hot' ? 'text-emerald-700' : v.tone === 'cold' ? 'text-rose-600' : 'text-foreground',
              )}
            >
              {v.display}
            </p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{v.phrase}</p>
            {v.relevanceNote && (
              <p className="mt-1 text-[10px] italic text-muted-foreground/70">Mostly silent at this horizon</p>
            )}
          </>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">Not yet measured</p>
        )}
      </div>

      {/* Noise floor (deadband) + source */}
      <div className="rounded-lg border border-border/60 bg-slate-50/60 px-4 py-3">
        <div className="flex items-center gap-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Noise floor
          </p>
          <HelpTooltip
            content="The deadband below which the target gap is treated as forecast noise — the engine takes no action. Band-based when measured from the forecast band; horizon fallback when no band is available yet."
            learnMore={learnMoreHref}
          />
        </div>
        <p className="mt-1 text-base font-bold tabular-nums text-foreground">
          {snap.deadbandPpt != null ? `±${snap.deadbandPpt}ppt` : '—'}
        </p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {deadbandLabel}
          {snap.deadbandSource === 'horizon' && ' (no band yet)'}
        </p>
      </div>
    </div>
  )
}

// ─── Day-type breakdown (D13): weekday vs weekend forecast / implied target / gap ────────────

function DayTypeBreakdownCard({ breakdown }: { breakdown: DayTypeBreakdown }) {
  // Each cell is a backend value rendered verbatim; '—' when a day type has no remaining dates.
  function fmt(v: number | null | undefined, suffix = '%'): string {
    return v == null ? '—' : `${v}${suffix}`
  }

  function gapCell(v: number | null | undefined) {
    if (v == null) return <span className="text-muted-foreground">—</span>
    const positive = v >= 0
    return (
      <span className={cn('font-semibold tabular-nums', positive ? 'text-emerald-600' : 'text-rose-600')}>
        {positive ? '+' : ''}
        {v}ppt
      </span>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Day type
          </TableHead>
          <TableHead className="text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Projected EOM
          </TableHead>
          <TableHead className="text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Implied target
          </TableHead>
          <TableHead className="text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Gap
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow>
          <TableCell className="text-sm font-medium text-foreground">Weekday</TableCell>
          <TableCell className="text-right text-sm tabular-nums text-foreground">
            {fmt(breakdown.forecast_weekday)}
          </TableCell>
          <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
            {fmt(breakdown.target_weekday)}
          </TableCell>
          <TableCell className="text-right text-sm">{gapCell(breakdown.gap_weekday)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className="text-sm font-medium text-foreground">Weekend</TableCell>
          <TableCell className="text-right text-sm tabular-nums text-foreground">
            {fmt(breakdown.forecast_weekend)}
          </TableCell>
          <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
            {fmt(breakdown.target_weekend)}
          </TableCell>
          <TableCell className="text-right text-sm">{gapCell(breakdown.gap_weekend)}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  )
}

// plain-language-ignore-end

// ─── ADR floor & projections (C7, D14/D16) ───────────────────────────────────

// Provenance copy per the design card / locked vocabulary:
//   explicit → "from your ADR target"
//   default  → "default: 85% of your recent £79 weekday ADR"
// Every monetary value is rendered exactly as the backend sent it (zero frontend math).
// At a combined inventory the reference is every listing's bookings, so it is the "building ADR"
// (`adrPhrase`, server-sent); everywhere else plain "ADR", exactly as before.
function floorProvenance(b: DayTypeBreakdown, sym: string, adrPhrase = 'ADR'): string {
  if (b.adr_floor_source === 'explicit') return 'From your ADR target'
  if (b.adr_floor_source === 'default') {
    const pct = b.adr_floor_pct ?? null
    // Prefer the weekday reference for the sentence (the dominant day type); fall back to weekend.
    const ref = b.adr_reference_weekday ?? b.adr_reference_weekend ?? null
    const refLabel = b.adr_reference_weekday != null ? 'weekday' : 'weekend'
    if (pct != null && ref != null) {
      return `Default: ${pct}% of your recent ${sym}${ref} ${refLabel} ${adrPhrase}`
    }
    return `Default, from your recent realized ${adrPhrase}`
  }
  return ''
}

function AdrFloorPanel({
  snap,
  currency,
  learnMoreHref,
  adrScope = null,
}: {
  snap: ForecastSnapshot
  currency: string
  learnMoreHref: string
  adrScope?: AdrScope | null
}) {
  const sym = currencySymbol(currency)
  const b = snap.dayTypeBreakdown
  const hasFloor = snap.adrFloorAmount != null
  const wdFloor = b?.adr_floor_weekday ?? null
  const weFloor = b?.adr_floor_weekend ?? null
  const showSplit = wdFloor != null && weFloor != null && wdFloor !== weFloor
  const provenance = b ? floorProvenance(b, sym, adrScope?.adrPhrase) : ''
  const limitedPct = snap.adrLimitedPct
  const guardedPct = snap.dilutionGuardedPct
  const hasProjections = snap.projectedAdr != null || b?.projected_revpar != null

  // Nothing C7/C8 to show on a pre-C7 snapshot — render nothing (backward-compatible).
  // A C8 guard rollup alone (no floor, no projections) still shows the floor card so the
  // dilution-guard note has a home.
  const hasGuardRollup = guardedPct != null && guardedPct > 0
  if (!hasFloor && !hasProjections && !hasGuardRollup) return null

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* ADR floor card */}
      <div className="rounded-xl border border-border/60 bg-white px-5 py-4 shadow-sm">
        <div className="flex items-center gap-1.5">
          <ShieldCheck className="h-4 w-4 text-[#0d9488]" />
          <p className="text-sm font-semibold text-foreground">ADR floor</p>
          <HelpTooltip
            content={
              adrScope
                ? `The rate Target Pressure will never discount below. When chasing your occupancy target would push a lead rate under the floor, the engine holds price at the floor instead. Set explicitly with an ADR target, or defaulted to a share of your own recent realized ${adrScope.adrPhrase}.${adrScope.floorSentence ? ` ${adrScope.floorSentence}` : ''}`
                : 'The rate Target Pressure will never discount below. When chasing your occupancy target would push a lead rate under the floor, the engine holds price at the floor instead. Set explicitly with an ADR target, or defaulted to a share of your own recent realized ADR.'
            }
            learnMore={learnMoreHref}
          />
        </div>

        {hasFloor ? (
          <>
            {showSplit ? (
              <div className="mt-2 flex items-baseline gap-5">
                <div>
                  <p className="text-2xl font-bold tabular-nums text-foreground">
                    {sym}{wdFloor}
                  </p>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Weekday</p>
                </div>
                <div>
                  <p className="text-2xl font-bold tabular-nums text-foreground">
                    {sym}{weFloor}
                  </p>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Weekend</p>
                </div>
              </div>
            ) : (
              <p className="mt-2 text-2xl font-bold tabular-nums text-foreground">
                {sym}{snap.adrFloorAmount}
              </p>
            )}
            {provenance && (
              <p className="mt-1 text-xs text-muted-foreground">{provenance}</p>
            )}
            {/* Whose prices the floor binds, at a combined inventory: the server's sentence. */}
            {adrScope?.floorSentence && <p className="mt-1 text-xs text-muted-foreground">{adrScope.floorSentence}</p>}
          </>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            No floor set — no ADR target and not enough realized history yet.
          </p>
        )}

        {limitedPct != null && limitedPct > 0 && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200/60 bg-amber-50/60 px-3 py-2">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            <p className="text-[11px] leading-relaxed text-amber-700">
              Held price at the floor on{' '}
              <span className="font-semibold tabular-nums">{Math.round(limitedPct * 100)}%</span>{' '}
              of steered dates — your occupancy target wanted deeper discounts than the floor allows.
            </p>
          </div>
        )}

        {/* C8 (D15) — dilution-guard rollup: discounts withheld on the price-inelastic close-in
            pool. Sky palette distinguishes it from the amber ADR-floor note; both are guardrails. */}
        {guardedPct != null && guardedPct > 0 && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-sky-200/60 bg-sky-50/60 px-3 py-2">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600" />
            <p className="text-[11px] leading-relaxed text-sky-700">
              Withheld a discount on{' '}
              <span className="font-semibold tabular-nums">{Math.round(guardedPct * 100)}%</span>{' '}
              of steered dates — close-in demand there books regardless of price, so a cut would only
              cheapen rooms that would have sold (the dilution guard).
            </p>
          </div>
        )}
      </div>

      {/* Display-only projections card (D16) */}
      <div className="rounded-xl border border-border/60 bg-white px-5 py-4 shadow-sm">
        <div className="flex items-center gap-1.5">
          <BadgePoundSterling className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-semibold text-foreground">Projected month economics</p>
          <HelpTooltip
            content="Where this month is heading on rate and revenue if current pickup continues at today's listed rates — realized and on-the-books revenue plus remaining pickup at current rates. Shown for context only; the engine does not steer on these projections."
            learnMore={learnMoreHref}
          />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-4">
          <div>
            <p className="text-2xl font-bold tabular-nums text-foreground">
              {snap.projectedAdr != null ? `${sym}${snap.projectedAdr}` : '—'}
            </p>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {adrScope ? `Projected ${adrScope.adrPhrase}` : 'Projected ADR'}
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold tabular-nums text-foreground">
              {b?.projected_revpar != null ? `${sym}${b.projected_revpar}` : '—'}
            </p>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Projected RevPAR</p>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">Display only — does not steer rates</p>
      </div>
    </div>
  )
}

// plain-language-ignore-start — the accuracy-ledger helpers (signedPpt, bucketNarration) and
// AccuracyLedgerPanel are diagnostics-only: rendered ONLY inside `<EngineDetails variant="section">`.
// Their `… ppt` forecast-error figures are verbatim Tier-3 raw values. See docs/revenue-vocabulary.md.
// ─── Accuracy ledger / forecast calibration (C9, D17) ────────────────────────

// One ppt figure, signed, with semantic colour. Over-forecast (positive error) reads rose;
// under-forecast (negative) reads emerald — same convention as the gap surfaces elsewhere.
// Pure display: the value is already rounded by the backend.
function signedPpt(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)} ppt`
}

// Plain-English summary of a bucket with measured history, e.g.
// "Over 3 months, projections at 8–21 days out have averaged −3.2 ppt; the engine now
//  trims +2.1 ppt." Read verbatim — no recomputation.
function bucketNarration(b: AccuracyLedgerBucket): string {
  if (b.nMonths <= 0 || b.meanErrorPpt == null) {
    return `No closed months measured at ${b.label.toLowerCase()} yet.`
  }
  const months = b.nMonths === 1 ? '1 month' : `${b.nMonths} months`
  const dir = b.meanErrorPpt >= 0 ? 'over-projected' : 'under-projected'
  const corr =
    b.appliedCorrectionPpt === 0
      ? 'no calibration applied yet'
      : `now calibrated by ${signedPpt(-b.appliedCorrectionPpt)}`
  return `Over ${months}, projections ${b.label.toLowerCase()} have ${dir} by ${signedPpt(
    b.meanErrorPpt,
  )} on average — ${corr}.`
}

function AccuracyLedgerPanel({
  ledger,
  learnMoreHref,
}: {
  ledger: AccuracyLedger
  learnMoreHref: string
}) {
  const currentRow = ledger.currentHorizonBucket
    ? ledger.buckets.find((b) => b.horizonBucket === ledger.currentHorizonBucket)
    : null

  return (
    <div className="rounded-xl border border-border/60 bg-white shadow-sm overflow-hidden">
      <div className="border-b border-border/40 px-5 py-4">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-[#0d9488]" />
          <p className="text-sm font-semibold text-foreground">Forecast accuracy</p>
          <HelpTooltip
            content="How honest the engine's projections have been at each horizon, measured as each month closes. Where the projections have consistently run high or low, the engine applies a calibration to the next forecast — gently at first, more confidently as months of evidence accumulate."
            learnMore={learnMoreHref}
          />
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Measured forecast error and the calibration applied, by days-until-month-end
        </p>
      </div>

      {!ledger.hasData ? (
        <div className="px-5 py-8 text-center">
          <Activity className="mx-auto h-8 w-8 text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium text-foreground">No accuracy history yet</p>
          <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
            As each month closes, the engine compares what it projected against what actually
            happened. After the first closed month, calibration begins — and strengthens as more
            evidence arrives. Until then, forecasts run uncalibrated.
          </p>
        </div>
      ) : (
        <>
          {/* This-month banner — the live applied correction for the current horizon */}
          {currentRow && ledger.currentAppliedPpt != null && (
            <div className="border-b border-border/40 bg-slate-50/60 px-5 py-3">
              <p className="text-sm text-foreground">
                This month is{' '}
                <span className="font-semibold tabular-nums">{ledger.currentDaysUntilEom}</span> days
                from close{' '}
                <span className="text-muted-foreground">({currentRow.label.toLowerCase()})</span>.{' '}
                {ledger.currentAppliedPpt === 0 ? (
                  <>No calibration is being applied at this horizon.</>
                ) : (
                  <>
                    The projection is calibrated by{' '}
                    <span
                      className={cn(
                        'font-semibold tabular-nums',
                        ledger.currentAppliedPpt > 0 ? 'text-rose-600' : 'text-emerald-600',
                      )}
                    >
                      {signedPpt(-ledger.currentAppliedPpt)}
                    </span>{' '}
                    based on its measured accuracy here.
                  </>
                )}
              </p>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Horizon</TableHead>
                <TableHead className="text-right">Months</TableHead>
                <TableHead className="text-right">Avg error</TableHead>
                <TableHead className="text-right">Typical miss</TableHead>
                <TableHead className="text-right">Calibration applied</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ledger.buckets.map((b) => {
                const measured = b.nMonths > 0 && b.meanErrorPpt != null
                const isCurrent = b.horizonBucket === ledger.currentHorizonBucket
                return (
                  <TableRow key={b.horizonBucket} className={cn(isCurrent && 'bg-teal-50/40')}>
                    <TableCell className="font-medium text-foreground">
                      {b.label}
                      {isCurrent && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-teal-100 px-1.5 py-0.5 text-[10px] font-medium text-teal-700">
                          This month
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm text-muted-foreground">
                      {measured ? b.nMonths : '—'}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right tabular-nums font-semibold',
                        measured
                          ? b.meanErrorPpt! > 0
                            ? 'text-rose-600'
                            : b.meanErrorPpt! < 0
                              ? 'text-emerald-600'
                              : 'text-foreground'
                          : 'text-muted-foreground',
                      )}
                    >
                      {measured ? signedPpt(b.meanErrorPpt!) : '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm text-muted-foreground">
                      {measured && b.maePpt != null ? `${b.maePpt.toFixed(1)} ppt` : '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-foreground">
                      {b.appliedCorrectionPpt === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        signedPpt(-b.appliedCorrectionPpt)
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>

          {/* Narration for the current horizon (or the most-measured one) */}
          {currentRow && currentRow.nMonths > 0 && (
            <div className="border-t border-border/40 px-5 py-3">
              <p className="text-xs leading-relaxed text-muted-foreground">{bucketNarration(currentRow)}</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// plain-language-ignore-end

// ─── Empty state (no saved target) ───────────────────────────────────────────

function NoTargetState({ propertyBasePath, monthName }: { propertyBasePath: string; monthName: string }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm animate-slide-in">
      <Target className="mx-auto h-8 w-8 text-muted-foreground/40" />
      <p className="mt-3 text-sm font-medium text-foreground">No target set for {monthName}</p>
      <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
        The rate engine is pricing this month using historical baselines only. Set a target to steer rates toward your occupancy target.
      </p>
      <div className="mt-4">
        <Link
          href={`${propertyBasePath}/targets`}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 transition-colors"
        >
          <Target className="h-3.5 w-3.5" />
          Set a target
        </Link>
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function TargetDetail({
  currentUser,
  property,
  properties,
  month,
  monthName,
  year,
  savedTargets,
  forecastSnapshots,
  latestSnapshot,
  affectedDates,
  accuracyLedger,
  adrScope = null,
}: TargetDetailProps) {
  const base = property.basePath
  const sym  = currencySymbol(property.currency)

  const hasTarget = savedTargets.occupancy != null || savedTargets.adr != null || savedTargets.revpar != null

  const stateCfg = latestSnapshot?.state ? STATE_CONFIG[latestSnapshot.state as StateKey] : null
  const StateIcon = stateCfg?.icon

  // Narration for the subtitle — locked v2 vocabulary
  function narrationFor(snap: ForecastSnapshot | null): string {
    if (!snap || !snap.state) return 'Forecast not yet available for this month'
    // Rounded for the sentence — `-18.97 pts` is a readout, not an answer. The exact figure
    // stays on the snapshot and inside the engine diagnostics below.
    const gapStr = snap.targetGap != null ? ` — ${gapPhrase(snap.targetGap)}` : ''
    switch (snap.state) {
      case 'on_track':  return `Projected end-of-month occupancy is close enough to target that there is nothing to do${gapStr}.`
      case 'pushing':   return `Projected end-of-month occupancy is below target${gapStr}. The engine is discounting to pull bookings in.`
      case 'easing':    return `Projected end-of-month occupancy is ahead of target${gapStr}. The engine is raising rates to earn more on what's left.`
      case 'at_floor':  return `Rates are already at your floor, so the engine has no room left to discount${gapStr}. To go further, lower the floor.`
      case 'dormant':   return `Target Pressure is standing by this month — see the reason below.`
      default:          return `Forecast detail for this month`
    }
  }

  return (
    <OperatorLayout title={`${monthName} ${year} target`} currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        {/* Back link */}
        <Link
          href={`${base}/targets`}
          className="animate-slide-in inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          style={{ animationDelay: '0ms' }}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Targets
        </Link>

        {/* Page header */}
        <div className="animate-slide-in" style={{ animationDelay: '30ms' }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold tracking-tight text-foreground">
                  {monthName} {year} — Occupancy
                </h1>
                <HelpTooltip
                  content="Forecast trajectory, current engine state, and upcoming rate adjustments driven by the occupancy target."
                  learnMore="/guides/targets#reading-the-dashboard-card"
                />
              </div>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {hasTarget ? narrationFor(latestSnapshot) : 'No target configured for this month'}
              </p>
              {/* D12 — booking velocity chip in the detail header (nil-safe, hidden pre-v3) */}
              {hasTarget && latestSnapshot && latestSnapshot.velocityRatio != null && (
                <div className="mt-2">
                  <VelocityChip
                    ratio={latestSnapshot.velocityRatio}
                    relevance={latestSnapshot.velocityRelevance}
                    learnMoreHref="/guides/targets#booking-velocity"
                  />
                </div>
              )}
            </div>

            {/* State badge */}
            {stateCfg && StateIcon && (
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold',
                  stateCfg.className
                )}>
                  <StateIcon className="h-3.5 w-3.5" />
                  {stateCfg.label}
                </span>
                {latestSnapshot?.capTrimmed && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200/60 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                    Trimmed by demand
                  </span>
                )}
                {latestSnapshot?.computedAt && (
                  <span className="text-[10px] text-muted-foreground/60">
                    Updated {formatTimeShort(latestSnapshot.computedAt)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* No-target state */}
        {!hasTarget && (
          <NoTargetState propertyBasePath={property.basePath} monthName={monthName} />
        )}

        {/* Main content — only shown when a target is saved.

            Two altitudes on ONE page:
            • TOP — the plain-language summary and the things an operator acts on (targets,
              projection vs target, ADR floor, affected dates, state callouts).
            • BASEMENT — a single collapsed "Engine diagnostics" disclosure holding the
              engine-internal statistics (confidence, forecast band / P10–P90, noise floor,
              day-type decomposition, forecast-accuracy ledger / calibration). Nothing there is
              a decision the operator makes; it is kept one click away for a system under test. */}
        {hasTarget && (
          <>
            {/* Summary stats — the operator-facing numbers */}
            <div
              className="animate-slide-in grid grid-cols-1 gap-4 sm:grid-cols-3"
              style={{ animationDelay: '50ms' }}
            >
              <StatCard
                label="Occ target"
                value={savedTargets.occupancy != null ? `${savedTargets.occupancy}%` : '—'}
                sub={savedTargets.adr != null ? `ADR: ${sym}${savedTargets.adr}` : undefined}
              />
              <StatCard
                label="Projected end of month"
                value={latestSnapshot?.forecastEom != null ? `${latestSnapshot.forecastEom.toFixed(1)}%` : '—'}
                sub="Latest forecast"
                gapDelta={latestSnapshot?.targetGap ?? null}
              />
              <StatCard
                label="At-floor rate"
                value={latestSnapshot?.atFloorPct != null
                  ? `${Math.round(latestSnapshot.atFloorPct * 100)}%`
                  : '—'}
                sub="Of upcoming dates at floor"
              />
            </div>

            {/* ADR floor & display-only projections (C7, D14/D16) — an operator-facing setting,
                so it stays on the glance surface. Renders nothing on pre-C7 snapshots. */}
            {latestSnapshot && (
              <div className="animate-slide-in" style={{ animationDelay: '75ms' }}>
                <AdrFloorPanel
                  snap={latestSnapshot}
                  currency={property.currency}
                  adrScope={adrScope}
                  learnMoreHref="/guides/targets#adr-floor"
                />
              </div>
            )}

            {/* Affected dates — the per-date changes the operator can act on */}
            <div
              className="animate-slide-in rounded-xl border border-border/60 bg-white shadow-sm overflow-hidden"
              style={{ animationDelay: '100ms' }}
            >
              <div className="border-b border-border/40 px-5 py-4">
                <p className="text-sm font-semibold text-foreground">Affected dates</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Upcoming dates where the engine is applying target pressure
                </p>
              </div>
              <AffectedDatesTable
                dates={affectedDates}
                totalCount={affectedDates.length}
                currency={property.currency}
                learnMoreHref="/guides/targets#adr-floor"
              />
            </div>

            {/* Dormant state detail */}
            {latestSnapshot?.state === 'dormant' && latestSnapshot.dormantReason && (
              <div
                className="animate-slide-in rounded-xl border border-border/60 bg-slate-50 shadow-sm p-5"
                style={{ animationDelay: '125ms' }}
              >
                <div className="flex gap-3">
                  <Info className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Why is Target Pressure dormant?</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {dormantReasonLabel(latestSnapshot.dormantReason)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* At-floor state detail */}
            {latestSnapshot?.state === 'at_floor' && (
              <div
                className="animate-slide-in rounded-xl border border-amber-200/60 bg-amber-50/50 shadow-sm p-5"
                style={{ animationDelay: '125ms' }}
              >
                <div className="flex gap-3">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Rates at floor</p>
                    <p className="mt-1 text-sm text-amber-700/80">
                      The engine has no further downward room — rates are against the price floor on{' '}
                      {latestSnapshot.atFloorPct != null
                        ? `${Math.round(latestSnapshot.atFloorPct * 100)}%`
                        : 'some'}{' '}
                      of upcoming dates. To allow further steering, lower the floor in{' '}
                      <Link
                        href={`${base}/pricing-rules/price-guardrails`}
                        className="font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900"
                      >
                        Price Guardrails
                      </Link>
                      .
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── Engine diagnostics (the basement) ──────────────────────────────────────────
                ONE collapsed disclosure — the only home for the engine-internal statistics.
                Everything below sinks here off the glance surface: confidence / signal strength,
                the forecast band (P10–P90), the noise floor, the weekday/weekend decomposition,
                the forecast trajectory chart, and the forecast-accuracy ledger (MAE + calibration).
                Collapsed by default; wrapped in `font-sans` so the rich panels render normally
                inside the disclosure's mono/technical shell. */}
            <div className="animate-slide-in" style={{ animationDelay: '150ms' }}>
              <EngineDetails variant="section" label="Engine diagnostics">
                <div className="space-y-5 font-sans text-sm normal-case tracking-normal text-foreground">
                  {/* Confidence / forecast band / booking velocity / noise floor (D11/D12) */}
                  {latestSnapshot && (
                    <LatestSnapshotPanel
                      snap={latestSnapshot}
                      learnMoreHref="/guides/targets#forecast-band"
                    />
                  )}

                  {/* Forecast trajectory — projected end-of-month over time, with the P10–P90 band */}
                  <div className="rounded-xl border border-border/60 bg-white shadow-sm">
                    <div className="border-b border-border/40 px-5 py-4">
                      <p className="text-sm font-semibold text-foreground">Forecast trajectory</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Projected end-of-month occupancy over time
                        {latestSnapshot && latestSnapshot.forecastP10 != null ? ', with forecast band,' : ''}{' '}
                        vs {savedTargets.occupancy != null ? `${savedTargets.occupancy}% target` : 'target'}
                      </p>
                    </div>
                    <div className="p-5">
                      <ForecastTrajectoryChart
                        snapshots={forecastSnapshots}
                        targetEom={savedTargets.occupancy}
                      />
                    </div>
                  </div>

                  {/* Weekday vs weekend day-type decomposition (D13) */}
                  {latestSnapshot?.dayTypeBreakdown &&
                    (latestSnapshot.dayTypeBreakdown.target_weekday != null ||
                      latestSnapshot.dayTypeBreakdown.target_weekend != null) && (
                      <div className="rounded-xl border border-border/60 bg-white shadow-sm overflow-hidden">
                        <div className="border-b border-border/40 px-5 py-4">
                          <div className="flex items-center gap-2">
                            <CalendarDays className="h-4 w-4 text-muted-foreground" />
                            <p className="text-sm font-semibold text-foreground">Weekday vs weekend</p>
                            <HelpTooltip
                              content="The engine decomposes your single occupancy target into implied weekday and weekend sub-targets using your property’s own historical day-type mix, then steers each date against its own day type. One near-full Saturday is not pushed on a midweek shortfall."
                              learnMore="/guides/targets#weekday-weekend"
                            />
                          </div>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            How the projected end-of-month and the implied target split across day types
                          </p>
                        </div>
                        <DayTypeBreakdownCard breakdown={latestSnapshot.dayTypeBreakdown} />
                      </div>
                    )}

                  {/* Forecast-accuracy ledger / calibration (C9, D17) */}
                  {accuracyLedger && (
                    <AccuracyLedgerPanel
                      ledger={accuracyLedger}
                      learnMoreHref="/guides/targets#forecast-accuracy"
                    />
                  )}
                </div>
              </EngineDetails>
            </div>
          </>
        )}
      </div>
    </OperatorLayout>
  )
}
